import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { format, subDays, startOfToday } from 'date-fns';
import { FaUsers, FaChartLine, FaMoneyBillWave, FaExchangeAlt } from 'react-icons/fa';
import { auth } from '../firebase/firebase';
import './Dashboard.css';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

export default function Dashboard() {
    const [dashboardData, setDashboardData] = useState({
        totalUsers: 0,
        totalUsersGrowth: 0,
        activeTransactions: 0,
        totalGMV: 0,
        totalGMVGrowth: 0,
        totalRevenue: 0,
        totalRevenueGrowth: 0
    });

    const [showMetricsModal, setShowMetricsModal] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState(null);
    const [timeframe, setTimeframe] = useState('week');
    const [metricsData, setMetricsData] = useState([]);
    const [dateRange, setDateRange] = useState({
        startDate: format(subDays(startOfToday(), 30), 'yyyy-MM-dd'),
        endDate: format(startOfToday(), 'yyyy-MM-dd')
    });

    const fetchDashboardData = async () => {
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/dashboard`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error('Failed to fetch dashboard data');
            }
            const data = await response.json();
            // Ensure all numeric fields are properly converted to numbers
            setDashboardData({
                totalUsers: Number(data.total_users) || 0,
                totalUsersGrowth: Number(data.total_users_growth) || 0,
                activeTransactions: Number(data.active_transactions) || 0,
                totalGMV: Number(data.total_gmv) || 0,
                totalGMVGrowth: Number(data.total_gmv_growth) || 0,
                totalRevenue: Number(data.total_revenue) || 0,
                totalRevenueGrowth: Number(data.total_revenue_growth) || 0
            });
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            // Set default values in case of error
            setDashboardData({
                totalUsers: 0,
                totalUsersGrowth: 0,
                activeTransactions: 0,
                totalGMV: 0,
                totalGMVGrowth: 0,
                totalRevenue: 0,
                totalRevenueGrowth: 0
            });
        }
    };

    const fetchMetricsData = async () => {
        if (!selectedMetric) return;

        try {
            const token = await auth.currentUser.getIdToken();
            let endpoint = '';
            
            switch (selectedMetric) {
                case 'users':
                    endpoint = 'user-growth-metrics';
                    break;
                case 'gmv':
                    endpoint = 'gmv-metrics';
                    break;
                case 'revenue':
                    endpoint = 'revenue-metrics';
                    break;
                default:
                    return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/${endpoint}?timeframe=${timeframe}&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const data = await response.json();
            setMetricsData(data);
        } catch (error) {
            console.error('Error fetching metrics data:', error);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        if (showMetricsModal && selectedMetric) {
            fetchMetricsData();
        }
    }, [selectedMetric, timeframe, dateRange, showMetricsModal]);

    const handleMetricClick = (metric) => {
        setSelectedMetric(metric);
        setShowMetricsModal(true);
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(value)) return 'Rp 0';
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0';
        return new Intl.NumberFormat('id-ID').format(value);
    };

    const formatGrowth = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0%';
        if (value === 0) return '0%';
        return `${value >= 0 ? '↑' : '↓'} ${Math.abs(Number(value)).toFixed(1)}%`;
    };

    const getChartData = () => {
        if (!metricsData.length) return null;

        const labels = metricsData.map(item => format(new Date(item.date), 'dd MMM yyyy'));
        const values = metricsData.map(item => item.value);

        return {
            labels,
            datasets: [
                {
                    label: selectedMetric === 'users' 
                        ? 'New Users' 
                        : selectedMetric === 'gmv'
                        ? 'GMV'
                        : 'Revenue',
                    data: values,
                    borderColor: '#4a90e2',
                    backgroundColor: 'rgba(74, 144, 226, 0.1)',
                    tension: 0.4,
                    fill: true
                }
            ]
        };
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: selectedMetric === 'users' 
                    ? 'User Growth' 
                    : selectedMetric === 'gmv'
                    ? 'GMV Trends'
                    : 'Revenue Trends'
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function(value) {
                        if (selectedMetric === 'users') return value;
                        return formatCurrency(value);
                    }
                }
            }
        }
    };

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <h1>Dashboard</h1>
                <p className="text-muted">Overview of your business metrics</p>
            </div>

            <div className="metrics-grid">
                <div className="metric-card" onClick={() => handleMetricClick('users')}>
                    <div className="metric-icon users">
                        <FaUsers />
                    </div>
                    <div className="metric-content">
                        <h3>Total Users</h3>
                        <div className="metric-value">{formatNumber(dashboardData.totalUsers)}</div>
                        <div className={`metric-growth ${dashboardData.totalUsersGrowth >= 0 ? 'positive' : 'negative'}`}>
                            {formatGrowth(dashboardData.totalUsersGrowth)}
                        </div>
                    </div>
                </div>

                <div className="metric-card">
                    <div className="metric-icon active-transactions">
                        <FaExchangeAlt />
                    </div>
                    <div className="metric-content">
                        <h3>Active Transactions</h3>
                        <div className="metric-value">{formatNumber(dashboardData.activeTransactions)}</div>
                        <div className="metric-label">Currently Active</div>
                    </div>
                </div>

                <div className="metric-card" onClick={() => handleMetricClick('gmv')}>
                    <div className="metric-icon gmv">
                        <FaChartLine />
                    </div>
                    <div className="metric-content">
                        <h3>Total GMV</h3>
                        <div className="metric-value">{formatCurrency(dashboardData.totalGMV)}</div>
                        <div className={`metric-growth ${dashboardData.totalGMVGrowth >= 0 ? 'positive' : 'negative'}`}>
                            {formatGrowth(dashboardData.totalGMVGrowth)}
                        </div>
                    </div>
                </div>

                <div className="metric-card" onClick={() => handleMetricClick('revenue')}>
                    <div className="metric-icon revenue">
                        <FaMoneyBillWave />
                    </div>
                    <div className="metric-content">
                        <h3>Total Revenue</h3>
                        <div className="metric-value">{formatCurrency(dashboardData.totalRevenue)}</div>
                        <div className={`metric-growth ${dashboardData.totalRevenueGrowth >= 0 ? 'positive' : 'negative'}`}>
                            {formatGrowth(dashboardData.totalRevenueGrowth)}
                        </div>
                    </div>
                </div>
            </div>

            {showMetricsModal && (
                <div className="modal-overlay" onClick={(e) => {
                    if (e.target.className === 'modal-overlay') {
                        setShowMetricsModal(false);
                        setSelectedMetric(null);
                    }
                }}>
                    <div className="modal-content">
                        <h2>
                            {selectedMetric === 'users' 
                                ? 'User Growth Metrics' 
                                : selectedMetric === 'gmv'
                                ? 'GMV Metrics'
                                : 'Revenue Metrics'}
                        </h2>

                        <div className="metrics-controls">
                            <div className="timeframe-selector">
                                <label>Timeframe:</label>
                                <select 
                                    value={timeframe} 
                                    onChange={(e) => setTimeframe(e.target.value)}
                                >
                                    <option value="day">Daily</option>
                                    <option value="week">Weekly</option>
                                    <option value="month">Monthly</option>
                                </select>
                            </div>

                            <div className="date-range">
                                <div className="date-input">
                                    <label>Start Date:</label>
                                    <input
                                        type="date"
                                        value={dateRange.startDate}
                                        onChange={(e) => setDateRange(prev => ({
                                            ...prev,
                                            startDate: e.target.value
                                        }))}
                                    />
                                </div>
                                <div className="date-input">
                                    <label>End Date:</label>
                                    <input
                                        type="date"
                                        value={dateRange.endDate}
                                        onChange={(e) => setDateRange(prev => ({
                                            ...prev,
                                            endDate: e.target.value
                                        }))}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="chart-container">
                            {getChartData() && (
                                <Line data={getChartData()} options={chartOptions} />
                            )}
                        </div>

                        <div className="modal-actions">
                            <button 
                                className="btn primary"
                                onClick={() => {
                                    setShowMetricsModal(false);
                                    setSelectedMetric(null);
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
